import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { LockIcon, SparklesIcon, XIcon } from 'lucide-react';
import type { Aggregates, AreaId, TrafficSlice } from '../../types/pulse';
import { areas, dataView, followUps, traffic } from '../../data/pulse';
import { followUpRows, rankFirstRows, ratingStats, sortedThemes } from '../../utils/pulse';
import { AREA_BAR, AREA_BORDER, AREA_SOFT, SERIES } from './colors';
import { InterpretationPanel } from './InterpretationPanel';
interface DataOverlayProps {
  aggregates: Aggregates;
  onClose: () => void;
  /** Internal/AbbVie view — congress delegates never see the AI interpretation. */
  internalView?: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;
function Section({
  title,
  kicker,
  children
}: {
  title: string;
  kicker?: string;
  children: React.ReactNode;
}) {
  return <section data-id="element-25" data-mp="2w7c0n828wh18">
      <div className="flex items-baseline justify-between gap-3" data-id="element-26" data-mp="2r668pcgesxsr">
        <h3 className="font-display text-[14px] font-semibold tracking-tight text-navy" data-id="element-27" data-mp="2r5wipovd6qn2">{title}</h3>
        {kicker && <span className="text-[10px] tabular-nums text-mut" data-id="element-28" data-mp="31zi0zrrrs9wz">{kicker}</span>}
      </div>
      <div className="mt-3" data-id="element-29" data-mp="35x7f988za0rt">{children}</div>
    </section>;
}
function BarRow({
  label,
  pct,
  count,
  color
}: {
  label: string;
  pct: number;
  count?: number;
  color: string;
}) {
  return <li data-id="element-30" data-mp="3fwf1kkxox5r2">
      <div className="flex items-baseline justify-between gap-3" data-id="element-31" data-mp="3fv3xsl48vxzy">
        <span className="min-w-0 flex-1 truncate text-[12px] leading-snug text-navy2" data-id="element-32" data-mp="2iar4ryxbolf0">{label}</span>
        <span className="shrink-0 font-display text-[13px] font-bold tabular-nums text-navy" data-id="element-33" data-mp="2n9av0biy8jd6">
          {pct}%
          {count !== undefined && <span className="ml-1.5 text-[10px] font-medium tabular-nums text-mut" data-id="element-34" data-mp="2wyc91w5xoqoi">{count}</span>}
        </span>
      </div>
      <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-line" data-id="element-35" data-mp="1zjq5taw7cpvg">
        <div className={`h-full rounded-full ${color}`} style={{
        width: `${pct}%`
      }} data-id="element-36" data-mp="24hrohuppun6e" />
      </div>
    </li>;
}
function StackedBar({
  parts
}: {
  parts: {
    id: string;
    pct: number;
    color: string;
  }[];
}) {
  return <div className="flex h-3 w-full overflow-hidden rounded-full bg-line" data-id="element-37" data-mp="1sgjqww494rru">
      {parts.map(part => <div key={part.id} className={part.color} style={{
      width: `${part.pct}%`
    }} data-id="element-38" data-mp="2663n1j3buqs6" />)}
    </div>;
}
function TrafficGroup({
  title,
  slices,
  offset
}: {
  title: string;
  slices: TrafficSlice[];
  offset: number;
}) {
  const total = slices.reduce((sum, s) => sum + s.count, 0);
  return <div className="rounded-xl border border-line bg-paper px-4 py-3.5" data-id="element-39" data-mp="19hxn39hcomk8">
      <p className="text-[9px] uppercase tracking-micro text-mut" data-id="element-40" data-mp="1ngdrgtrqq0z7">{title}</p>
      <ul className="mt-2.5 flex flex-col gap-2" data-id="element-41" data-mp="1neawrwfn7jo5">
        {slices.map((slice, i) => <BarRow key={slice.id} label={slice.label} pct={total ? Math.round(slice.count / total * 100) : 0} count={slice.count} color={SERIES[(i + offset) % SERIES.length]} data-id="element-42" data-mp="1ijnggkuslvkn" data-mpi={i} />)}
      </ul>
    </div>;
}
export function DataOverlay({
  aggregates,
  onClose,
  internalView = false
}: DataOverlayProps) {
  const [aiOpen, setAiOpen] = useState(false);
  const journey = rankFirstRows(aggregates, null);
  const journeyTotal = areas.reduce((sum, a) => sum + aggregates.rankFirst[a.id], 0);
  const team = ratingStats(aggregates.team);
  const tech = ratingStats(aggregates.tech);
  const themes = sortedThemes(aggregates);
  const themeLeader = themes[0]?.count ?? 1;
  const ratingCards = [{
    label: 'Wider care-team models',
    stat: team,
    tally: aggregates.team,
    bar: 'bg-teal',
    tint: 'bg-teal/10 border-teal/40'
  }, {
    label: 'Technology',
    stat: tech,
    tally: aggregates.tech,
    bar: 'bg-blue',
    tint: 'bg-blue/10 border-blue/40'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18,
    ease: EASE
  }} className="fixed inset-0 z-50 overflow-y-auto bg-navy/45 px-4 py-8 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-label={dataView.title} data-id="element-43" data-mp="1iaihafca7vr8">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 8
    }} transition={{
      duration: 0.22,
      ease: EASE
    }} className="mx-auto w-full max-w-lg overflow-hidden rounded-[24px] bg-paper shadow-soft" data-id="element-44" data-mp="3ll58kj7tsxhb">
        <div className="flex items-start justify-between gap-4 bg-navy px-6 py-5" data-id="element-45" data-mp="d5li1ryjht5y">
          <div className="min-w-0" data-id="element-46" data-mp="d7puwzef22id">
            <p className="text-[9px] uppercase tracking-micro text-mint" data-id="element-47" data-mp="8ed72rhk9zqo">Live results</p>
            <h2 className="mt-2 font-display text-[19px] font-semibold leading-snug tracking-tight text-paper" data-id="element-48" data-mp="3g1x8gr9np8g">
              {dataView.title}
            </h2>
            <p className="mt-2 text-[11.5px] leading-snug text-sky" data-id="element-49" data-mp="10wio4o3nlw9a">{dataView.subtitle}</p>
            <p className="mt-3 font-display text-[28px] font-bold leading-none tabular-nums text-mint" data-id="element-50" data-mp="w369n6sov5u9">
              {journeyTotal}
              <span className="ml-2 font-sans text-[11px] font-medium text-sky" data-id="element-51" data-mp="18ep688axdqhw">
                delegates so far
              </span>
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={dataView.close} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-paper/30 text-paper/80 transition-colors duration-150 hover:border-mint hover:text-paper focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" data-id="element-52" data-mp="1dci9asgxm4dy">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-53" data-mp="1w5m6uox9t1du" />
          </button>
        </div>

        {internalView && <div className="border-b border-line bg-mist px-6 py-4" data-id="element-54" data-mp="212kxuyln0s7t">
            <button type="button" onClick={() => setAiOpen(true)} className="flex w-full items-center gap-3 rounded-xl border border-navy/25 bg-paper px-4 py-3 text-left transition-colors duration-150 hover:border-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-55" data-mp="2560s5oa8ighc">
              <SparklesIcon className="h-4 w-4 shrink-0 text-blue" aria-hidden="true" data-id="element-56" data-mp="5y4v87bqrpqq" />
              <span className="min-w-0 flex-1" data-id="element-57" data-mp="jsxhrpq1u66t">
                <span className="block font-display text-[13.5px] font-semibold leading-none tracking-tight text-navy" data-id="element-58" data-mp="f0e1gkzz8wyb">
                  AI Interpretation
                </span>
                <span className="mt-1.5 block text-[11px] leading-snug text-mut" data-id="element-59" data-mp="oqphv70ukym3">
                  What could these results mean for AbbVie?
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1 rounded-full bg-navy px-2 py-1 text-[8.5px] uppercase tracking-micro text-paper" data-id="element-60" data-mp="12o4d4sds80lz">
                <LockIcon className="h-2.5 w-2.5" aria-hidden="true" data-id="element-61" data-mp="34h222d4fu91n" />
                Internal
              </span>
            </button>
          </div>}

        <div className="flex flex-col gap-7 px-6 py-6" data-id="element-62" data-mp="1f0py9v5vcbmd">
          <Section title={dataView.sections.journey} kicker={`${journeyTotal} rankings`} data-id="element-63" data-mp="1f21400ivjtg4">
            <StackedBar parts={journey.map(row => ({
            id: row.id,
            pct: row.pct,
            color: AREA_BAR[row.id as AreaId]
          }))} data-id="element-64" data-mp="1jyhp0lus1roz" />
            <ul className="mt-3 flex flex-col gap-2.5" data-id="element-65" data-mp="1a8gb2mw51pns">
              {journey.map(row => <BarRow key={row.id} label={`${row.label} — ranked #1`} pct={row.pct} color={AREA_BAR[row.id as AreaId]} data-id="element-66" data-mp="w8ndc7m7296b" />)}
            </ul>
          </Section>

          <Section title={dataView.sections.followUp} data-id="element-67" data-mp="ralvtr4f2hwy">
            <div className="grid gap-3 sm:grid-cols-3" data-id="element-68" data-mp="rd827nfkmrdl">
              {areas.map(area => <div key={area.id} className={`rounded-xl border px-4 py-3.5 ${AREA_SOFT[area.id]} ${AREA_BORDER[area.id]}`} data-id="element-69" data-mp="2hocq93rtomka">
                  <p className="flex items-center gap-2 text-[11.5px] font-semibold text-navy" data-id="element-70" data-mp="14gq6te4fgyvm">
                    <span className={`h-2.5 w-2.5 rounded-sm ${AREA_BAR[area.id]}`} aria-hidden="true" data-id="element-71" data-mp="14j2p5se3lb97" />
                    {area.label}
                  </p>
                  <ul className="mt-2.5 flex flex-col gap-2" data-id="element-72" data-mp="qpuutsymu1nt">
                    {followUpRows(aggregates, area.id, null).map(row => <BarRow key={row.id} label={followUps[area.id].options.find(o => o.id === row.id)?.label ?? row.label} pct={row.pct} color={AREA_BAR[area.id]} data-id="element-73" data-mp="lonnfv0ej4ho" />)}
                  </ul>
                </div>)}
            </div>
          </Section>

          <Section title={dataView.sections.ratings} kicker="1–5 scale" data-id="element-74" data-mp="2enm5g87i6uew">
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-75" data-mp="2el9o9x9kko2w">
              {ratingCards.map(item => <div key={item.label} className={`rounded-xl border px-4 py-3.5 ${item.tint}`} data-id="element-76" data-mp="2nfece8xbjleq">
                  <p className="text-[11.5px] font-semibold leading-snug text-navy" data-id="element-77" data-mp="2nhquqn6znxsb">{item.label}</p>
                  <p className="mt-2 font-display text-[30px] font-bold leading-none tabular-nums text-navy" data-id="element-78" data-mp="2sgbwir1w2piv">
                    {item.stat.average}
                    <span className="ml-1 font-display text-[13px] font-semibold text-mut" data-id="element-79" data-mp="1zqlcy2hhmvfd">/ 5</span>
                  </p>
                  <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-paper" data-id="element-80" data-mp="24nlkkhtzk68l">
                    <div className={`h-full rounded-full ${item.bar}`} style={{
                  width: `${Number(item.stat.average) / 5 * 100}%`
                }} data-id="element-81" data-mp="29pkktiufin1b" />
                  </div>
                  <p className="mt-2 text-[11px] leading-snug text-navy2" data-id="element-82" data-mp="3l4ziypydjky0">
                    <span className="font-semibold tabular-nums" data-id="element-83" data-mp="2r4kqbr0kton9">{item.stat.highPct}%</span> rated 4
                    or 5 · {item.tally.n} responses
                  </p>
                </div>)}
            </div>
          </Section>

          <Section title={dataView.sections.themes} kicker={`${aggregates.openResponses} open responses`} data-id="element-84" data-mp="34w93lxmkaj5t">
            <ul className="flex flex-col gap-2.5" data-id="element-85" data-mp="287t1l229mhbt">
              {themes.map((theme, i) => <BarRow key={theme.id} label={theme.label} pct={Math.round(theme.count / themeLeader * 100)} count={theme.count} color={SERIES[i % SERIES.length]} data-id="element-86" data-mp="2d64e5kjy35xq" data-mpi={i} />)}
            </ul>
            <p className="mt-3 text-[10.5px] leading-snug text-mut" data-id="element-87" data-mp="3sljv0oi0v17a">
              Bars show each theme relative to the most mentioned one.
            </p>
          </Section>

          <Section title={dataView.sections.traffic} data-id="element-88" data-mp="bins4yeyzwt2">
            <div className="grid gap-3 sm:grid-cols-3" data-id="element-89" data-mp="bljqgnuqcni1">
              <TrafficGroup title={dataView.trafficLabels.day} slices={traffic.day} offset={0} data-id="element-90" data-mp="6lxbo9z96z74" />
              <TrafficGroup title={dataView.trafficLabels.daypart} slices={traffic.daypart} offset={1} data-id="element-91" data-mp="1sl1v5z8gwyf" />
              <TrafficGroup title={dataView.trafficLabels.source} slices={traffic.source} offset={2} data-id="element-92" data-mp="3oz4y3uhvth28" />
            </div>
          </Section>
        </div>
      </motion.div>

      <AnimatePresence data-id="element-93" data-mp="3f17ddgz8e2ed">
        {aiOpen && <InterpretationPanel aggregates={aggregates} onClose={() => setAiOpen(false)} data-id="element-94" data-mp="3ayu80toec6i4" />}
      </AnimatePresence>
    </motion.div>;
}